import { cn } from '@/shared/lib/cn';
import { formatDateTime } from '@/shared/lib/format';
import type { GuestProfileResponse } from '@/shared/api';
import { GuestBillingBlock } from './GuestBillingBlock';
import { ID_KIND_LABEL } from './guestLabels';
import { onboardingDone } from './helpers';
import { SectionCard } from './SectionCard';

const PII_LABELS: Array<{ label: string; key: keyof GuestProfileResponse }> = [
  { label: 'E-Mail', key: 'email' },
  { label: 'Telefon', key: 'phone' },
  { label: 'Geburtsdatum', key: 'dateOfBirth' },
  { label: 'Nationalität', key: 'nationality' },
  { label: 'Ausweis-Typ', key: 'identityKind' },
  { label: 'Ausweis-Nr.', key: 'identityNumber' },
  { label: 'Ausstellungsland', key: 'identityIssuingCountry' },
  { label: 'Straße', key: 'streetAddress' },
  { label: 'PLZ', key: 'postalCode' },
  { label: 'Stadt', key: 'city' },
  { label: 'Land', key: 'country' },
];

/** Gastprofil (Board „BuchungGast“): Person, Ausweis, Anschrift, Rechnungsdaten, Mitreisende. */
export function GuestProfileCard({ guest }: { guest: GuestProfileResponse }) {
  const done = onboardingDone(guest);
  const companionNationalities = guest.foreignCompanionNationalities ?? [];
  const value = (key: keyof GuestProfileResponse) =>
    key === 'identityKind' && guest.identityKind
      ? ID_KIND_LABEL[guest.identityKind]
      : (guest[key] as string | null);
  return (
    <SectionCard
      icon="Users"
      title="Gastprofil"
      trailing={
        <span
          className={cn(
            'rounded-pill px-2.5 py-1 text-xs font-bold',
            done ? 'bg-forest-soft text-forest-deep' : 'bg-amber-tint text-amber-ink',
          )}
        >
          {done ? 'Onboarding abgeschlossen' : 'Onboarding offen'}
        </span>
      }
    >
      <div>
        <p className="font-serif text-xl font-bold">
          {guest.firstName} {guest.lastName}
        </p>
        <p className="text-ink-soft mt-1 text-xs">
          {guest.bookingType === 'B2B' ? 'Geschäftliche Buchung' : 'Privatbuchung'} · vom Gast im
          Onboarding eingegeben, verschlüsselt gespeichert
          {guest.onboardingCompletedAt && (
            <> · abgegeben {formatDateTime(guest.onboardingCompletedAt)}</>
          )}
        </p>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        {PII_LABELS.map(({ label, key }) => (
          <div key={label}>
            <dt className="text-ink-soft font-mono text-[10px] font-semibold uppercase tracking-[0.12em]">
              {label}
            </dt>
            <dd className="break-words">{value(key) ?? '—'}</dd>
          </div>
        ))}
      </dl>
      <GuestBillingBlock guest={guest} />
      {guest.foreignCompanionCount > 0 && (
        // Aggregat laut §29 Abs. 2 BMG – erhoben werden Anzahl und Staatsangehörigkeiten, keine Namen.
        <div className="border-hairline border-t pt-3 text-sm">
          <p className="font-bold">Mitreisende ({guest.foreignCompanionCount})</p>
          {companionNationalities.length > 0 && (
            <p className="text-ink-soft mt-1">
              Staatsangehörigkeiten:{' '}
              <span className="font-mono">{companionNationalities.join(', ')}</span>
            </p>
          )}
        </div>
      )}
    </SectionCard>
  );
}
